import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PublicHeader from './PublicHeader';
import { fetchPublicProperties } from './publicPropertyApi';
import './PublicHome.css';

function PublicHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = React.useState('');
  const [bedFilter, setBedFilter] = React.useState('any');

  const [listings, setListings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [listError, setListError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setListError('');
      const { data, error } = await fetchPublicProperties();
      if (cancelled) return;
      if (error) {
        setListError(error.message || String(error));
        setListings([]);
      } else {
        setListings(data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() =>
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      );
    }
  }, [location.pathname, location.hash]);

  const filtered = listings.filter((p) => {
    const q = query.trim().toLowerCase();
    const textOk =
      !q ||
      p.title.toLowerCase().includes(q) ||
      p.location.toLowerCase().includes(q);
    const bedOk =
      bedFilter === 'any' ||
      (bedFilter === '3+' ? p.beds >= 3 : String(p.beds) === bedFilter);
    return textOk && bedOk;
  });

  const bathLabel = (b) =>
    b != null ? `${b} bath${b !== 1 ? 's' : ''}` : '—';
  const sqftLabel = (s) => (s != null ? `${s} sq ft` : '—');

  return (
    <div className="ph-root">
      <PublicHeader />

      <section className="ph-hero">
        <div className="ph-hero-glow" aria-hidden="true" />
        <div className="ph-hero-inner">
          <p className="ph-eyebrow">Curated rentals · ODUS multi-let</p>
          <h1 className="ph-hero-title">
            Find a place that feels like{' '}
            <span className="ph-hero-accent">place</span>.
          </h1>
          <p className="ph-hero-lead">
            Explore verified listings with clear pricing, room counts, and
            locations across the city. No clutter—just beautiful spaces.
          </p>
          <div className="ph-hero-cta">
            <a href="#listings" className="ph-btn ph-btn-primary ph-btn-lg">
              View all properties
            </a>
          </div>
          <dl className="ph-stats">
            <div className="ph-stat">
              <dt className="ph-stat-label">Live listings</dt>
              <dd className="ph-stat-value">{loading ? '…' : String(listings.length)}</dd>
            </div>
            <div className="ph-stat">
              <dt className="ph-stat-label">Avg. response</dt>
              <dd className="ph-stat-value">&lt; 24h</dd>
            </div>
            <div className="ph-stat">
              <dt className="ph-stat-label">Areas covered</dt>
              <dd className="ph-stat-value">12</dd>
            </div>
          </dl>
        </div>
      </section>

      <section id="listings" className="ph-listings">
        <div className="ph-section-head">
          <div>
            <h2 className="ph-section-title">Available properties</h2>
            <p className="ph-section-sub">
              Listings loaded from your Supabase <code>properties</code> table.
            </p>
          </div>
          <div className="ph-filters">
            <label className="ph-search">
              <span className="ph-visually-hidden">Search</span>
              <span className="ph-search-icon" aria-hidden="true">
                ⌕
              </span>
              <input
                type="search"
                className="ph-search-input"
                placeholder="Search by name or area…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="ph-select-wrap">
              <span className="ph-visually-hidden">Bedrooms</span>
              <select
                className="ph-select"
                value={bedFilter}
                onChange={(e) => setBedFilter(e.target.value)}
              >
                <option value="any">Any beds</option>
                <option value="1">1 bed</option>
                <option value="2">2 beds</option>
                <option value="3+">3+ beds</option>
              </select>
            </label>
          </div>
        </div>

        {listError ? (
          <p className="ph-empty" role="alert">
            {listError}
          </p>
        ) : null}

        {loading ? (
          <p className="ph-empty">Loading listings…</p>
        ) : null}

        {!loading && !listError && filtered.length === 0 ? (
          <p className="ph-empty">
            No properties in the database yet, or no matches—try another search.
          </p>
        ) : null}

        {!loading && !listError && filtered.length > 0 ? (
          <ul className="ph-grid">
            {filtered.map((p) => (
              <li key={p.id}>
                <article
                  className="ph-card ph-card--clickable"
                  role="link"
                  tabIndex={0}
                  aria-label={`View ${p.title}`}
                  onClick={() => navigate(`/property/${p.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/property/${p.id}`);
                    }
                  }}
                >
                  <div className="ph-card-media">
                    <img
                      src={p.image}
                      alt=""
                      className="ph-card-img"
                      loading="lazy"
                      decoding="async"
                    />
                    {p.tag && (
                      <span className={`ph-badge ph-badge--${p.tag === 'New' ? 'new' : 'feat'}`}>
                        {p.tag}
                      </span>
                    )}
                  </div>
                  <div className="ph-card-body">
                    <h3 className="ph-card-title">{p.title}</h3>
                    <p className="ph-card-loc">{p.location}</p>
                    <ul className="ph-meta">
                      <li>
                        {p.beds} bed{p.beds !== 1 ? 's' : ''}
                      </li>
                      <li>{bathLabel(p.baths)}</li>
                      <li>{sqftLabel(p.sqft)}</li>
                    </ul>
                    <div className="ph-card-foot">
                      <span className="ph-rent">
                        £{p.rent.toLocaleString()}
                        <span className="ph-rent-unit">/mo</span>
                      </span>
                      <span className="ph-btn ph-btn-soft ph-card-cta" aria-hidden="true">
                        View details
                      </span>
                    </div>
                  </div>
                </article>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section id="how" className="ph-how">
        <h2 className="ph-section-title ph-section-title--center">
          Simple from browse to keys
        </h2>
        <ol className="ph-steps">
          <li className="ph-step">
            <span className="ph-step-num">1</span>
            <h3 className="ph-step-title">Browse</h3>
            <p className="ph-step-text">
              Filter by area and size. Every listing shows real photos and
              monthly rent upfront.
            </p>
          </li>
          <li className="ph-step">
            <span className="ph-step-num">2</span>
            <h3 className="ph-step-title">Enquire</h3>
            <p className="ph-step-text">
              Send a message to the team. We aim to reply within one business
              day.
            </p>
          </li>
          <li className="ph-step">
            <span className="ph-step-num">3</span>
            <h3 className="ph-step-title">Move in</h3>
            <p className="ph-step-text">
              Viewings, paperwork, and handover—handled with clear timelines.
            </p>
          </li>
        </ol>
      </section>
    </div>
  );
}

export default PublicHome;
